/**
 * Tenant Utilities
 * Helper functions for tenant identification from subdomain
 */

/**
 * Extract subdomain from current host
 * @returns {string|null} - Subdomain or null if not found
 *
 * Examples:
 * - localhost:3000 → null (dev mode)
 * - demo.fleetmanager.com → 'demo'
 * - admin.fleetmanager.com → 'admin' (superuser portal)
 * - acme.fleetmanager.com → 'acme'
 */
export function getSubdomainFromHost() {
  const host = window.location.hostname;

  // Development mode - configurable default tenant
  if (host === 'localhost' || host === '127.0.0.1') {
    // 1. Check for localStorage override (for dev testing specific tenant)
    const devSubdomain = localStorage.getItem('fm_dev_subdomain');
    if (devSubdomain) {
      return devSubdomain;
    }
    // 2. Check for environment variable (set at build time)
    // CRITICAL: If REACT_APP_DEFAULT_TENANT_SUBDOMAIN is set, login is LOCKED to that tenant!
    // Leave empty/unset to allow login to ANY tenant (generic login screen)
    const envSubdomain = process.env.REACT_APP_DEFAULT_TENANT_SUBDOMAIN;
    if (envSubdomain) {
      return envSubdomain;
    }
    // 3. No default = generic login, user can log in to any tenant
    return null;
  }

  // Production - extract subdomain from hostname
  const parts = host.split('.');

  // Need at least 3 parts: subdomain.domain.tld
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    return subdomain;
  }

  // No subdomain found
  return null;
}

/**
 * Check if current subdomain is the admin portal
 */
export function isAdminPortal() {
  const subdomain = getSubdomainFromHost();
  return subdomain === 'admin';
}

/**
 * Set development subdomain (for testing)
 */
export function setDevSubdomain(subdomain) {
  if (subdomain) {
    localStorage.setItem('fm_dev_subdomain', subdomain);
  } else {
    localStorage.removeItem('fm_dev_subdomain');
  }
  // Reload to apply
  window.location.reload();
}

/**
 * Get full tenant URL
 */
export function getTenantUrl(subdomain, path = '') {
  // In development, just use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `${window.location.origin}${path}`;
  }

  // In production, construct subdomain URL
  const parts = window.location.hostname.split('.');
  if (parts.length >= 3) {
    // Replace first part with new subdomain
    parts[0] = subdomain;
    return `${window.location.protocol}//${parts.join('.')}${path}`;
  }

  // Fallback
  return `${window.location.origin}${path}`;
}

const tenantUtils = {
  getSubdomainFromHost,
  isAdminPortal,
  setDevSubdomain,
  getTenantUrl,
};

export default tenantUtils;
